import { apiService } from './ApiService';
import type { OnePager } from '../types';

// Convert backend one-pager format to frontend format
const convertBackendOnePager = (backendOnePager: any): OnePager => {
  return {
    id: backendOnePager.id.toString(),
    title: backendOnePager.title,
    description: backendOnePager.description || '',
    content: backendOnePager.content || '',
    spec: typeof backendOnePager.spec === 'string' 
      ? JSON.parse(backendOnePager.spec) 
      : backendOnePager.spec,
    authorId: backendOnePager.creator_id?.toString(),
    creator_id: backendOnePager.creator_id,
    is_template: backendOnePager.is_template,
    created_at: backendOnePager.created_at,
    updated_at: backendOnePager.updated_at,
    clone_count: backendOnePager.clone_count ?? 0,
    build_count: backendOnePager.build_count ?? 0,
    creator: backendOnePager.creator ? {
      id: backendOnePager.creator.id.toString(),
      email: backendOnePager.creator.email,
      fullName: backendOnePager.creator.fullName,
      username: backendOnePager.creator.username
    } : undefined
  };
};

// Convert frontend one-pager format to backend format
const convertToBackendOnePager = (frontendOnePager: Partial<OnePager>) => {
  return {
    title: frontendOnePager.title || '',
    description: frontendOnePager.description || '',
    content: frontendOnePager.content || '',
    spec: frontendOnePager.spec || {},
    is_template: frontendOnePager.is_template || false
  };
};

export class OnePagerDataService {
  async getAllOnePagers(): Promise<OnePager[]> {
    try {
      const backendOnePagers = await apiService.getAllOnePagers() as any[];
      return backendOnePagers.map(convertBackendOnePager);
    } catch (error) {
      console.error('Error fetching one-pagers:', error);
      throw error;
    }
  }

  async getTemplates(): Promise<OnePager[]> {
    try {
      const backendTemplates = await apiService.getTemplates() as any[];
      return backendTemplates.map(convertBackendOnePager);
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  async getOnePagerById(id: string): Promise<OnePager | null> {
    try {
      const backendOnePager = await apiService.getOnePagerById(id);
      return convertBackendOnePager(backendOnePager);
    } catch (error: any) {
      if (error.message.includes('404') || error.message.includes('not found')) {
        return null;
      }
      console.error('Error fetching one-pager:', error);
      throw error;
    }
  }

  async createOnePager(onePager: Omit<OnePager, 'id' | 'authorId'>): Promise<OnePager> {
    try {
      const backendData = convertToBackendOnePager(onePager);
      const createdOnePager = await apiService.createOnePager(backendData);
      return convertBackendOnePager(createdOnePager);
    } catch (error) {
      console.error('Error creating one-pager:', error);
      throw error;
    }
  }

  async updateOnePager(id: string, updates: Partial<OnePager>): Promise<OnePager> {
    try {
      const backendData = convertToBackendOnePager(updates);
      const updatedOnePager = await apiService.updateOnePager(id, backendData);
      return convertBackendOnePager(updatedOnePager);
    } catch (error) {
      console.error('Error updating one-pager:', error);
      throw error;
    }
  }

  async deleteOnePager(id: string): Promise<void> {
    try {
      await apiService.deleteOnePager(id);
    } catch (error) {
      console.error('Error deleting one-pager:', error);
      throw error;
    }
  }

  async createFromTemplate(templateId: string, customizations?: Partial<OnePager>): Promise<OnePager> {
    try {
      const backendCustomizations = customizations ? convertToBackendOnePager(customizations) : undefined;
      const createdOnePager = await apiService.createFromTemplate(templateId, backendCustomizations);
      return convertBackendOnePager(createdOnePager);
    } catch (error) {
      console.error('Error creating from template:', error);
      throw error;
    }
  }
}

export const onePagerDataService = new OnePagerDataService();